import { api } from '@goaltree/backend/convex/_generated/api'
import { cn } from '@goaltree/ui/lib/utils'
import { useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import type { ReactNode } from 'react'

// Admin-only, so copy stays in English rather than going through i18n.
type Engagement = NonNullable<FunctionReturnType<typeof api.admin.getEngagement>>

const pct = (n: number, of: number) => (of > 0 ? Math.round((n / of) * 100) : 0)

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-widest text-foreground/40">
          {title}
        </h2>
        {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
      </div>
      {children}
    </section>
  )
}

function Tile({ label, value, sub }: { label: string; value: number; sub?: string }) {
  return (
    <div className="rounded-2xl border border-border/60 bg-card/40 px-3 py-3">
      <p className="text-[11px] font-semibold text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-2xl font-black tabular-nums text-foreground">{value}</p>
      {sub && <p className="text-[11px] tabular-nums text-muted-foreground">{sub}</p>}
    </div>
  )
}

function Bar({ label, n, of, tone }: { label: string; n: number; of: number; tone?: string }) {
  const p = pct(n, of)
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="truncate text-foreground">{label}</span>
        <span className="shrink-0 tabular-nums text-muted-foreground">
          <span className="font-semibold text-foreground">{n}</span> · {p}%
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-foreground/[0.07]">
        <div
          className={cn('h-full rounded-full', tone ?? 'bg-primary')}
          style={{ width: `${p}%` }}
        />
      </div>
    </div>
  )
}

function Ratio({ v }: { v: number[] }) {
  const [active, eligible] = v
  if (!eligible) return <span className="text-foreground/25">—</span>
  const p = pct(active, eligible)
  return (
    <span className="tabular-nums">
      <span
        className={cn(
          'font-semibold',
          p >= 40 ? 'text-primary-text' : p >= 20 ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        {p}%
      </span>
      <span className="text-muted-foreground/70">
        {' '}
        {active}/{eligible}
      </span>
    </span>
  )
}

function Daily({ days }: { days: number[] }) {
  const max = Math.max(1, ...days)
  return (
    <div className="flex h-24 items-end gap-1">
      {days.map((n, i) => (
        <div key={i} className="flex flex-1 flex-col items-center gap-1">
          <span className="text-[10px] tabular-nums text-muted-foreground">{n}</span>
          <div
            className={cn(
              'w-full rounded-t',
              i === days.length - 1 ? 'bg-primary' : 'bg-primary/40',
            )}
            style={{ height: `${Math.max(2, (n / max) * 64)}px` }}
          />
        </div>
      ))}
    </div>
  )
}

const LAST_SEEN: { key: keyof Engagement['lastSeen']; label: string; tone: string }[] = [
  { key: 'day', label: 'Today', tone: 'bg-primary' },
  { key: 'week', label: 'This week', tone: 'bg-primary/60' },
  { key: 'month', label: 'This month', tone: 'bg-amber-500/70' },
  { key: 'older', label: 'Older', tone: 'bg-foreground/25' },
  { key: 'never', label: 'Never used', tone: 'bg-destructive/60' },
]

function Body({ e }: { e: Engagement }) {
  const total = e.totalUsers
  const nv = e.never
  return (
    <div className="flex flex-col gap-8">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Tile label="Users" value={total} />
        <Tile label="Active today" value={e.active.d1} sub={`${pct(e.active.d1, total)}%`} />
        <Tile label="Active 7d" value={e.active.d7} sub={`${pct(e.active.d7, total)}%`} />
        <Tile label="Active 30d" value={e.active.d30} sub={`${pct(e.active.d30, total)}%`} />
      </div>

      <Section
        title="Daily active · last 14 days"
        hint="Created, completed, coached or brain dumped."
      >
        <Daily days={e.daily} />
      </Section>

      <Section title="Activation" hint="Share of all signups who ever…">
        <Bar label="Did anything after signup" n={e.funnel.didAnything} of={total} />
        <Bar label="Created a real goal" n={e.funnel.createdGoal} of={total} />
        <Bar label="Came back after day 1" n={e.funnel.cameBack} of={total} />
        <Bar label="Completed a task or habit" n={e.funnel.completed} of={total} />
      </Section>

      <Section title="Last seen">
        <div className="flex h-3 overflow-hidden rounded-full">
          {LAST_SEEN.map(({ key, tone }) => (
            <div key={key} className={tone} style={{ width: `${pct(e.lastSeen[key], total)}%` }} />
          ))}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
          {LAST_SEEN.map(({ key, label, tone }) => (
            <span key={key} className="flex items-center gap-1.5 text-muted-foreground">
              <span className={cn('size-2 rounded-full', tone)} />
              {label}
              <span className="font-semibold tabular-nums text-foreground">{e.lastSeen[key]}</span>
            </span>
          ))}
        </div>
      </Section>

      <Section
        title="Retention by signup week"
        hint="Active in days 1–7, 8–14 and 22–28 after signup. — means too recent."
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-semibold text-muted-foreground">
                <th className="py-1.5 pr-2 font-semibold">Week of</th>
                <th className="py-1.5 pr-2 text-right font-semibold">Signups</th>
                <th className="py-1.5 pr-2 text-right font-semibold">Wk 1</th>
                <th className="py-1.5 pr-2 text-right font-semibold">Wk 2</th>
                <th className="py-1.5 text-right font-semibold">Wk 4</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {e.cohorts.map((c) => (
                <tr key={c.week}>
                  <td className="py-1.5 pr-2 tabular-nums text-foreground">
                    {new Date(c.week).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      timeZone: 'UTC',
                    })}
                  </td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">{c.n}</td>
                  <td className="py-1.5 pr-2 text-right">
                    <Ratio v={c.w1} />
                  </td>
                  <td className="py-1.5 pr-2 text-right">
                    <Ratio v={c.w2} />
                  </td>
                  <td className="py-1.5 text-right">
                    <Ratio v={c.w4} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Features used · last 7 days" hint={`Of ${e.active.d7} active users.`}>
        <Bar label="Created goals or tasks" n={e.using7d.create} of={e.active.d7} />
        <Bar label="Completed tasks or habits" n={e.using7d.complete} of={e.active.d7} />
        <Bar label="Messaged the coach" n={e.using7d.coach} of={e.active.d7} />
        <Bar label="Brain dump" n={e.using7d.braindump} of={e.active.d7} />
      </Section>

      <Section
        title="Never started"
        hint={`${nv.total} signed up and never did anything — who are they?`}
      >
        <Bar label="Signed up with Google" n={nv.google} of={nv.total} tone="bg-destructive/60" />
        <Bar label="Signed up with email" n={nv.email} of={nv.total} tone="bg-destructive/60" />
        {nv.other > 0 && (
          <Bar label="Other / no account" n={nv.other} of={nv.total} tone="bg-destructive/60" />
        )}
        <Bar label="Email verified" n={nv.emailVerified} of={nv.total} tone="bg-foreground/40" />
        <Bar label="Finished onboarding" n={nv.onboarded} of={nv.total} tone="bg-foreground/40" />
        <Bar label="Can get push" n={nv.pushable} of={nv.total} tone="bg-foreground/40" />
        <Bar
          label="Signed up in last 7d"
          n={nv.signedUpLast7d}
          of={nv.total}
          tone="bg-foreground/40"
        />
        <Bar
          label="Signed up in last 30d"
          n={nv.signedUpLast30d}
          of={nv.total}
          tone="bg-foreground/40"
        />
        {nv.authMissing > 0 && (
          <p className="text-xs text-muted-foreground">
            {nv.authMissing} have no auth record (deleted accounts).
          </p>
        )}
      </Section>

      <Section title="Reachability">
        <Bar label="Lapsed (not active 7d) with push" n={e.lapsed.pushable} of={e.lapsed.total} />
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span>
            Signup method · Google{' '}
            <span className="font-semibold tabular-nums text-foreground">
              {e.signupMethod.google}
            </span>
          </span>
          <span>
            Email{' '}
            <span className="font-semibold tabular-nums text-foreground">
              {e.signupMethod.email}
            </span>
          </span>
          {e.signupMethod.other > 0 && (
            <span>
              Other{' '}
              <span className="font-semibold tabular-nums text-foreground">
                {e.signupMethod.other}
              </span>
            </span>
          )}
        </div>
      </Section>
    </div>
  )
}

export function AdminStats() {
  const e = useQuery(api.admin.getEngagement)
  if (e === undefined)
    return (
      <div className="flex flex-col gap-8">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-[84px] animate-pulse rounded-2xl border border-border/60 bg-card/40"
            />
          ))}
        </div>
        {[120, 140, 40].map((h, i) => (
          <div key={i} className="flex flex-col gap-3">
            <div className="h-2.5 w-36 animate-pulse rounded bg-muted/60" />
            <div className="animate-pulse rounded-xl bg-muted/30" style={{ height: h }} />
          </div>
        ))}
      </div>
    )
  if (!e) return null
  return <Body e={e} />
}
